import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MetricInfo from '../components/MetricInfo';
import api from '../api/client';
import AppLayout from '../components/AppLayout';
import EquityChart from '../components/EquityChart';
import { Skeleton } from '../components/ui/skeleton';

function fmtDate(s) { return s ? s.slice(0, 10) : '—'; }
function fmtPct(v)  { return v != null ? `${v > 0 ? '+' : ''}${v.toFixed(2)}%` : '—'; }
function fmtNum(v)  { return v != null ? v.toFixed(2) : '—'; }

function MetricsGrid({ metrics, label }) {
  if (!metrics) return null;
  const items = [
    { key: 'Total Return', info: 'total_return_pct', val: fmtPct(metrics.total_return_pct), color: metrics.total_return_pct >= 0 ? 'text-win' : 'text-loss' },
    // Training windows are often well under a year, where nothing can be
    // annualized honestly — the backend leaves such spans un-extrapolated and
    // the label follows it.
    {
      key: metrics.cagr_is_annualized === false ? 'Period Return' : 'CAGR',
      info: 'cagr_pct',
      val: fmtPct(metrics.cagr_pct),
      color: metrics.cagr_pct >= 0 ? 'text-win' : 'text-loss',
    },
    { key: 'Max Drawdown', info: 'max_drawdown_pct', val: `-${fmtNum(metrics.max_drawdown_pct)}%`, color: 'text-loss' },
    { key: 'Sharpe', info: 'sharpe_ratio', val: fmtNum(metrics.sharpe_ratio) },
    { key: 'Sortino', info: 'sortino_ratio', val: fmtNum(metrics.sortino_ratio) },
    // Read beside Sharpe: at low exposure that ratio is substantially
    // measuring the days the strategy sat in cash.
    {
      key: 'Exposure',
      info: 'time_in_market_pct',
      val: metrics.time_in_market_pct != null ? `${metrics.time_in_market_pct.toFixed(0)}%` : '—',
      color: metrics.time_in_market_pct != null && metrics.time_in_market_pct < 50 ? 'text-loss' : undefined,
    },
    { key: 'Win Rate', info: 'win_rate_pct', val: `${metrics.win_rate_pct?.toFixed(1)}%` },
    { key: 'Trades', val: metrics.total_trades },
    { key: 'Expectancy', info: 'expectancy_r', val: metrics.expectancy_r != null ? `${metrics.expectancy_r.toFixed(2)}R` : '—' },
    { key: 'Profit Factor', info: 'profit_factor', val: fmtNum(metrics.profit_factor) },
    { key: 'Avg Hold', val: `${metrics.avg_holding_days?.toFixed(1)}d` },
  ];
  return (
    <div className="flex flex-col gap-2">
      <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="grid grid-cols-4 gap-2">
        {items.map(({ key, val, color, info }) => (
          <div key={key} className="flex flex-col gap-0.5 p-2 bg-surface-base rounded border border-border">
            <span className={`text-sm font-semibold ${color || 'text-foreground'}`}>{val}</span>
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              {key}{info && <MetricInfo metric={info} />}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function HelmRunResult() {
  const { id, runId } = useParams();

  const [run,         setRun]         = useState(null);
  const [results,     setResults]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [selectedId, setSelectedId]  = useState(null);

  useEffect(() => {
    api.get(`/helms/${id}/runs/${runId}`)
      .then(res => {
        setRun(res.data.run);
        setResults(res.data.results);
        if (res.data.results.length > 0) {
          const top = res.data.results.find(r => r.promoted) || res.data.results[0];
          setSelectedId(top.id);
        }
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load run'))
      .finally(() => setLoading(false));
  }, [id, runId]);

  const selected = results.find(r => r.id === selectedId);

  const subtitle = run
    ? `Training ${fmtDate(run.training_start)} → ${fmtDate(run.training_end)}  ·  Testing ${fmtDate(run.testing_start)} → ${fmtDate(run.testing_end)}  ·  Rank by ${run.rank_by}`
    : '';

  if (loading) return (
    <AppLayout>
      <div className="p-6 max-w-5xl mx-auto flex flex-col gap-4">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-4 w-1/2" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-14 rounded" />)}
        </div>
        <Skeleton className="h-48 w-full rounded" />
      </div>
    </AppLayout>
  );
  if (error)   return <AppLayout><p className="p-8 text-sm text-destructive">{error}</p></AppLayout>;

  return (
    <AppLayout backTo={`/helms/${id}`} backLabel="← Back to Helm">
      <main className="p-6 flex flex-col gap-6 max-w-5xl">
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}

        {/* Training Ranking Table */}
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-medium text-foreground">
            Training Ranking — all {results.length} pair{results.length !== 1 ? 's' : ''}
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-border bg-surface-base">
                  {[
                    { h: 'Rank' }, { h: 'Screener' }, { h: 'Trade Config' }, { h: 'Score' },
                    { h: 'Sharpe', info: 'sharpe_ratio' },
                    { h: 'Sortino', info: 'sortino_ratio' },
                    { h: 'CAGR', info: 'cagr_pct' },
                    { h: 'Max DD', info: 'max_drawdown_pct' },
                    { h: 'Exposure', info: 'time_in_market_pct' },
                    { h: 'Win Rate', info: 'win_rate_pct' },
                    { h: 'Trades' }, { h: 'Testing' },
                  ].map(({ h, info }) => (
                    <th key={h} className="text-left px-3 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">
                      <span className="flex items-center gap-1">{h}{info && <MetricInfo metric={info} />}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {results.map(r => {
                  const m = r.training_metrics || {};
                  const isTested = r.testing_metrics != null;
                  const isSel = r.id === selectedId;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setSelectedId(r.id)}
                      className={`border-b border-border/50 cursor-pointer transition-colors ${isSel ? 'bg-primary/10' : 'hover:bg-surface-hover'}`}
                    >
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {r.promoted
                          ? <span title={run?.fallback_reason ? `Fallback pair — ranking refused: ${run.fallback_reason}` : 'Promoted pair'}>★ {r.training_rank}</span>
                          : r.training_rank}
                      </td>
                      <td className="px-3 py-2 text-xs text-foreground font-medium">{r.screener_name}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{r.trade_config_name || <span className="opacity-40">—</span>}</td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.training_score?.toFixed(4)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{fmtNum(m.sharpe_ratio)}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{fmtNum(m.sortino_ratio)}</td>
                      <td className={`px-3 py-2 text-xs ${m.cagr_pct >= 0 ? 'text-win' : 'text-loss'}`}>{fmtPct(m.cagr_pct)}</td>
                      <td className="px-3 py-2 text-xs text-loss">-{fmtNum(m.max_drawdown_pct)}%</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{m.time_in_market_pct != null ? `${m.time_in_market_pct.toFixed(0)}%` : '—'}</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{m.win_rate_pct?.toFixed(1)}%</td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">{m.total_trades}</td>
                      <td className="px-3 py-2">
                        {isTested
                          ? <span className="text-xs px-1.5 py-0.5 rounded bg-win/15 text-win">Tested</span>
                          : <span className="text-xs text-muted-foreground/40">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">Click a row to view its details below.</p>
        </div>

        {/* Selected Pair Detail */}
        {selected && (
          <div className="flex flex-col gap-4 border-t border-border pt-6">
            <h3 className="text-sm font-medium text-foreground">
              {selected.screener_name}
              {selected.trade_config_name && (
                <span className="text-muted-foreground"> × {selected.trade_config_name}</span>
              )}
              {' '}
              <span className="text-xs font-normal px-1.5 py-0.5 rounded bg-primary/10 text-primary">Rank #{selected.training_rank}</span>
              {selected.promoted && run?.fallback_reason && (
                <span className="ml-1 text-xs font-normal px-1.5 py-0.5 rounded bg-orange-400/15 text-orange-600" title={run.fallback_reason}>Fallback</span>
              )}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <MetricsGrid metrics={selected.training_metrics} label="Training" />
              {selected.testing_metrics
                ? <MetricsGrid metrics={selected.testing_metrics} label="Testing ★" />
                : <div className="flex items-center justify-center text-sm text-muted-foreground border border-border rounded p-4">No testing run</div>}
            </div>

            {(selected.equity_curve || []).length > 0 && (
              <div className="flex flex-col gap-2">
                <h4 className="text-xs text-muted-foreground">
                  {selected.testing_metrics ? 'Testing' : 'Training'} Equity Curve
                </h4>
                <div height={220}>
                  <EquityChart
                    data={selected.equity_curve}
                    initialFunds={selected.testing_metrics
                      ? selected.testing_metrics.initial_funds
                      : selected.training_metrics?.initial_funds || 100000}
                  />
                </div>
              </div>
            )}

            {(selected.testing_trades || []).length > 0 && (
              <div className="flex flex-col gap-2">
                <h4 className="text-sm font-medium text-foreground">Testing Trades</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-surface-base">
                        {['Symbol', 'Entry', 'Exit', 'Shares', 'Entry ₹', 'Exit ₹', 'P&L', 'P&L %', 'Hold', 'Reason'].map(h => (
                          <th key={h} className="text-left px-3 py-2 text-xs font-medium text-muted-foreground whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selected.testing_trades.map((t, i) => (
                        <tr key={i} className="border-b border-border/50 hover:bg-surface-hover">
                          <td className="px-3 py-1.5 text-xs font-medium text-foreground">{t.symbol.replace('.NS', '')}</td>
                          <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{fmtDate(t.entry_date)}</td>
                          <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{fmtDate(t.exit_date)}</td>
                          <td className="px-3 py-1.5 text-xs text-muted-foreground">{t.shares}</td>
                          <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">₹{t.entry_price?.toFixed(2)}</td>
                          <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">₹{t.exit_price?.toFixed(2)}</td>
                          <td className={`px-3 py-1.5 text-xs ${t.pnl > 0 ? 'text-win' : 'text-loss'}`}>₹{t.pnl?.toFixed(0)}</td>
                          <td className={`px-3 py-1.5 text-xs ${t.pnl_pct > 0 ? 'text-win' : 'text-loss'}`}>{fmtPct(t.pnl_pct)}</td>
                          <td className="px-3 py-1.5 text-xs text-muted-foreground">{t.holding_days}d</td>
                          <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{t.exit_reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </AppLayout>
  );
}
